import axios from "axios";
import { SignJWT } from "jose";
import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha256";

import App from "app";
import api from "core/api/api";
import propertyTemplateService from "core/services/metaStoreService/property-template-service";
import store from "src/stores/redux/store";
import {
    setAuthHeadersAction,
    setConfigurationAction,
    setHealthStatusAction,
    setIsEnabledAction,
    setIsLoadingAction
} from "src/stores/redux/features/striim-ai/striim-ai.slice";

interface CopilotAuthClaims {
    striim_version: string;
    product_key: string;
    license_key: string;
    cluster_name: string;
    company_name: string;
    user_id: string;
}

const getCopilotAuthClaims = (): CopilotAuthClaims => {
    const { version, licenseKey, productKey, clusterName, companyName } = propertyTemplateService.getServerInfo();
    return {
        striim_version: version ?? "",
        product_key: productKey ?? "",
        license_key: licenseKey ?? "",
        cluster_name: clusterName ?? "",
        company_name: companyName ?? "",
        user_id: App.user?.name ?? ""
    };
};

let lastInitEndpointAccessible = true;
const signingKeyCache = new Map<string, { key: string | null; timestamp: number }>();
const pendingSigningKeyFetches = new Map<string, Promise<string | null>>();
const SIGNING_KEY_CACHE_TTL = 60 * 60 * 1000; // 1 hour, for a successfully fetched key
const SIGNING_KEY_FAILURE_TTL = 30 * 1000; // cooldown after a failed fetch, so the health poll doesn't retry an unreachable /do/init on every cycle

const base64urlEncodeBytes = (bytes: Uint8Array): string => {
    return btoa(String.fromCharCode(...Array.from(bytes)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
};

const base64urlEncode = (data: string): string => {
    return base64urlEncodeBytes(new TextEncoder().encode(data));
};

// crypto.subtle (Web Crypto) is only exposed in secure contexts (HTTPS, localhost).
// On plain-HTTP deployments accessed by hostname/IP, it's undefined, so jose (which
// relies on it in the browser) can't run either — fall back to a pure-JS HMAC.
const isSubtleCryptoAvailable = (): boolean => typeof crypto !== "undefined" && !!crypto.subtle;

export const signJwt = async (claims: CopilotAuthClaims, key: string): Promise<string | null> => {
    try {
        if (isSubtleCryptoAvailable()) {
            const secret = new TextEncoder().encode(key);
            return await new SignJWT({ ...claims }).setProtectedHeader({ alg: "HS256" }).sign(secret);
        }

        const header = { alg: "HS256", typ: "JWT" };
        const headerEncoded = base64urlEncode(JSON.stringify(header));
        const payloadEncoded = base64urlEncode(JSON.stringify(claims));

        const keyData = new TextEncoder().encode(key);
        const messageData = new TextEncoder().encode(`${headerEncoded}.${payloadEncoded}`);
        const signature = hmac(sha256, keyData, messageData);

        const signatureEncoded = base64urlEncodeBytes(signature);
        return `${headerEncoded}.${payloadEncoded}.${signatureEncoded}`;
    } catch (err) {
        console.error("JWT signing failed:", err);
        return null;
    }
};

const fetchSigningKey = async (
    baseUrl: string,
    productKey: string,
    trackAvailability = true
): Promise<string | null> => {
    const cached = signingKeyCache.get(productKey);
    if (cached) {
        const ttl = cached.key ? SIGNING_KEY_CACHE_TTL : SIGNING_KEY_FAILURE_TTL;
        if (Date.now() - cached.timestamp < ttl) {
            return cached.key;
        }
    }

    // De-dupe concurrent callers (e.g. recheckConfiguration fetching health + auth headers
    // in parallel) so they share one /do/init request instead of firing one each.
    const pending = pendingSigningKeyFetches.get(productKey);
    if (pending) {
        return pending;
    }

    const fetchPromise = (async () => {
        try {
            const initUrl = new URL(baseUrl.replace("/do/ai", "/do/init"));
            initUrl.searchParams.set("product_key", productKey);
            const response = await axios.get<{ key: string }>(initUrl.toString());
            const key = response.data?.key ?? null;

            signingKeyCache.set(productKey, { key, timestamp: Date.now() });
            if (trackAvailability) setInitEndpointAccessibility(!!key);
            return key;
        } catch (err) {
            console.error("Failed to fetch signing key from /do/init:", err);
            signingKeyCache.set(productKey, { key: null, timestamp: Date.now() });
            if (trackAvailability) setInitEndpointAccessibility(false);
            return null;
        } finally {
            pendingSigningKeyFetches.delete(productKey);
        }
    })();

    pendingSigningKeyFetches.set(productKey, fetchPromise);
    return fetchPromise;
};

// trackAvailability should stay true only for calls against the user's actually-configured
// copilotUrl — the shared isInitEndpointAccessible flag drives the main connection status,
// and must not flip because an unrelated server (e.g. the demo/config-catalog server) was unreachable.
export const getAuthHeaders = async (copilotUrl: string, trackAvailability = true): Promise<Record<string, string>> => {
    try {
        const claims = getCopilotAuthClaims();
        const productKey = claims.product_key;

        const signingKey = await fetchSigningKey(copilotUrl, productKey, trackAvailability);
        if (!signingKey) {
            return {};
        }

        const token = await signJwt(claims, signingKey);
        if (!token) {
            return {};
        }

        return { Authorization: `Bearer ${token}` };
    } catch (err) {
        console.error("Failed to create auth headers:", err);
        return {};
    }
};

export const isInitEndpointAccessible = (): boolean => {
    return lastInitEndpointAccessible;
};

export const setInitEndpointAccessibility = (accessible: boolean): void => {
    lastInitEndpointAccessible = accessible;
};

export interface HealthStatus {
    status: HealthStatusEnum;
    service: string;
    timestamp: number;
    isAuth: boolean;
    agent: AgentHealthStatus;
}

type HealthStatusEnum = "healthy" | "degraded" | "unhealthy" | "unavailable";

export const getStriimAIDemoUrl = (): string => {
    const { version } = propertyTemplateService.getServerInfo();
    return version?.indexOf("SNAPSHOT") !== -1
        ? "https://ui-demo-in.striim.com:9090/do/ai"
        : "https://ai.striim.cloud/do/ai";
};

export interface AgentHealthStatus {
    status: HealthStatusEnum;
    ok: boolean;
    service: string;
    chat_model_available: boolean;
    embed_model_available: boolean;
    knowledgebase_enabled: boolean;
    agents_count: number;
    agents: string[];
    error?: string | null;
}
export interface EngineDetails {
    engineType?: string;
    model?: string;
    provider?: string;
    ollamaURL?: string | null;
    striimAiUrl?: string | null;
}

export interface ConfigurationResult {
    isConfigured: boolean;
    copilotUrl: string | null;
    healthStatus: HealthStatus | null;
    engineDetails: EngineDetails | null;
}

interface CopilotServiceInterface {
    init: () => Promise<void>;
    isAIAssistantsEnabled: boolean;
    fetchHealthStatus: (copilotUrl: string, trackAvailability?: boolean) => Promise<HealthStatus | null>;
    fetchEngineDetails: () => Promise<EngineDetails | null>;
    recheckConfiguration: () => Promise<ConfigurationResult | null>;
    pollUntilHealthy: (maxTries?: number, intervalMs?: number) => Promise<HealthStatus | null>;
    getAuthHeaders: (copilotUrl: string, trackAvailability?: boolean) => Promise<Record<string, string>>;
}

class CopilotService implements CopilotServiceInterface {
    isAIAssistantsEnabled = false;
    isPollingUntilHealthy = false;
    providerConfig = null;

    async getAuthHeaders(copilotUrl: string, trackAvailability = true): Promise<Record<string, string>> {
        return getAuthHeaders(copilotUrl, trackAvailability);
    }

    async init(): Promise<void> {
        try {
            this.isAIAssistantsEnabled = await api.shouldEnableAIAssistants();
        } catch (e) {
            console.error(e);
        }
        store.dispatch(setIsEnabledAction(this.isAIAssistantsEnabled));
        if (this.isAIAssistantsEnabled) {
            // await should not be added here ,
            // it would block the other execution
            this.recheckConfiguration(true);
        }
    }
    async fetchProviderConfig() {
        try {
            // Always hits the demo/catalog server regardless of the user's own configuration,
            // so it must never affect the shared init-endpoint-accessible status.
            const demoUrl = getStriimAIDemoUrl();
            const configUrl = demoUrl.replace("/do/ai", "/do/config");
            const headers = await getAuthHeaders(demoUrl, false);
            const response = await axios.get(configUrl, { headers });

            this.providerConfig = response.data?.providers ?? null;
        } catch (err) {
            return null;
        }
    }

    async fetchHealthStatus(copilotUrl: string, trackAvailability = true): Promise<HealthStatus | null> {
        try {
            const healthCheckUrl = copilotUrl.replace("/do/ai", "/do/health");
            const headers = await getAuthHeaders(copilotUrl, trackAvailability);
            const response = await axios.get<HealthStatus>(healthCheckUrl, { headers });
            return response.data;
        } catch {
            return null;
        }
    }

    async fetchEngineDetails(): Promise<EngineDetails | null> {
        try {
            return await api.getConfiguredCopilotEngineDetails();
        } catch {
            return null;
        }
    }

    async recheckConfiguration(initialCheck = false): Promise<ConfigurationResult | null> {
        try {
            initialCheck && store.dispatch(setIsLoadingAction(true));
            store.dispatch(setHealthStatusAction(null));

            const configured = await api.isCopilotConfigured();
            let result: ConfigurationResult;
            if (!configured) {
                store.dispatch(setAuthHeadersAction(null));
                result = { isConfigured: false, copilotUrl: null, healthStatus: null, engineDetails: null };
            } else {
                const url = (await api.getCopilotURL()) ?? null;
                const [healthStatus, engineDetails, authHeaders] = await Promise.all([
                    url ? this.fetchHealthStatus(url) : Promise.resolve(null),
                    this.fetchEngineDetails(),
                    url ? getAuthHeaders(url) : Promise.resolve(null)
                ]);
                store.dispatch(setAuthHeadersAction(url ? authHeaders : null));
                result = { isConfigured: true, copilotUrl: url, healthStatus, engineDetails };
            }
            store.dispatch(setConfigurationAction(result));
            return result;
        } catch (err) {
            console.error("Error is ", err);
            return null;
        } finally {
            initialCheck && store.dispatch(setIsLoadingAction(false));
        }
    }

    async pollHealth(): Promise<HealthStatus | null> {
        if (this.isPollingUntilHealthy) return null;
        const isConfigured = store.getState().striimAi.isConfigured;
        if (!isConfigured) return null;

        const copilotUrl = store.getState().striimAi.copilotUrl;
        if (!copilotUrl) {
            const healthStatus = store.getState().striimAi.healthStatus;
            if (healthStatus) {
                store.dispatch(setHealthStatusAction(null));
            }
        }
        const healthStatus = await this.fetchHealthStatus(copilotUrl as string);
        store.dispatch(setHealthStatusAction(healthStatus));
        return healthStatus;
    }

    async pollUntilHealthy(maxTries = 120, intervalMs = 5_000): Promise<HealthStatus | null> {
        this.isPollingUntilHealthy = true;
        try {
            let healthStatus = null;
            for (let i = 0; i < maxTries; i++) {
                const copilotUrl = store.getState().striimAi.copilotUrl;
                if (!copilotUrl) break;
                const health = await this.fetchHealthStatus(copilotUrl);
                healthStatus = health;
                if (health?.agent?.status !== "unavailable") {
                    store.dispatch(setHealthStatusAction(health));
                    return health;
                }
                if (i < maxTries - 1) {
                    await new Promise<void>(resolve => setTimeout(resolve, intervalMs));
                }
            }
            store.dispatch(setHealthStatusAction(healthStatus));
            return healthStatus;
        } finally {
            this.isPollingUntilHealthy = false;
        }
    }
}

const copilotService = new CopilotService();
export default copilotService;
